import { useContext, useEffect, useState } from 'react';
import { Button, TextField } from 'darrius';

import { CheckoutContext } from '../../../../context/Checkout';
import { axiosInstance } from '../../../../utils/axiosInstance';
import { showToast } from '../../../atoms/Toast/Toast';
import { customTracking } from '../../../../utils/customTracking';

import styles from './BoletoView.module.scss';

export const BoletoView = ({ origin }) => {
  const { checkoutData, updateCheckoutData } = useContext(CheckoutContext);
  const [loading, setLoading] = useState(false);
  const [boletoCode, setBoletoCode] = useState('');
  const [showCoupon, setShowCoupon] = useState(false);

  const activeBundle = checkoutData.bundlesData.find(
    (bundle) => bundle.nameId === checkoutData.currentBundleName
  );

  async function handleGenerateBoletoCode() {
    setLoading(true);
    try {
      const requestOptions = {
        method: 'POST',
        url: `${process.env.NEXT_PUBLIC_API_PRO}/purchase`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          authToken: checkoutData.authToken,
          paymentMethod: 'ticket',
          bundleOption: activeBundle.purchaseLink,
          coupon: showCoupon ? checkoutData.coupon : '',
          origin,
        },
      };

      const { data } = await axiosInstance(requestOptions);
      setBoletoCode(data.barcode);
      setLoading(false);
      if (window?.newrelic) {
        window.newrelic.addPageAction('webcheckout_action', {
          step: 'boleto_generate_code_success',
          bundle_id: activeBundle?.id,
          invoice_id: data.invoice_partner_id,
        });
      }
      customTracking({
        schema:
          'iglu:br.com.getninjas/bank_slip_copy_code_click/jsonschema/1-0-0',
        data: {
          profile_id: checkoutData.profile.id,
          bundle_id: activeBundle.id,
          action: 'click',
          invoice_id: data.invoice_partner_id,
          step: 'boleto_payment',
          label: 'copy_code_click_sucess',
        },
      });
    } catch (err) {
      setLoading(false);
      if (window?.newrelic) {
        window.newrelic.noticeError(err, {
          step: 'boleto_generate_code_error',
          bundle_id: activeBundle?.id,
          status: err?.response?.status,
          message: err?.response?.data?.clientMessage || err?.message,
        });
        window.newrelic.addPageAction('webcheckout_action', {
          step: 'boleto_generate_code_error',
          bundle_id: activeBundle?.id,
          status: err?.response?.status,
          message: err?.response?.data?.clientMessage || err?.message,
        });
      }
    }
  }

  function handleCopyBoletoCode() {
    navigator.clipboard.writeText(boletoCode);
    showToast({ message: 'Código copiado com sucesso!', type: 'success' });
  }

  async function handleApplyDiscounts() {
    try {
      const { data } = await axiosInstance({
        method: 'POST',
        url: `${process.env.NEXT_PUBLIC_API_PRO}/purchase/discounts`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          authToken: checkoutData.authToken,
          bundle: activeBundle.purchaseLink,
          coupon: checkoutData.coupon,
          origin,
        },
      });

      const bundleWithDiscounts = {
        ...activeBundle,
        totalPrice: data.current_value,
        installmentValue: data.current_installments_details.max_part_price,
        installmentsLabel: data.current_value_installments,
        installments: data.installments,
      };

      updateCheckoutData((oldState) => {
        const bundles = oldState.bundlesData.filter(
          (bundle) => bundle.nameId !== checkoutData.currentBundleName
        );

        return {
          ...oldState,
          bundlesData: [...bundles, bundleWithDiscounts].sort(
            (a, b) => a.id - b.id
          ),
        };
      });
      showToast({
        type: 'success',
        message: 'Cupom aplicado',
      });
    } catch (error) {
      showToast({
        type: 'error',
        message:
          error.response.data.clientMessage ||
          'Não foi possivel aplicar o cupom',
      });
    }
  }

  useEffect(() => {
    customTracking({
      schema: 'iglu:br.com.getninjas/payment_tab_click/jsonschema/1-0-0',
      data: {
        payment_type: 'bankslip',
        profile_id: checkoutData.profile.id,
        bundle_id: activeBundle.id,
      },
    });
  }, []);

  return (
    <div className={`boleto-view ${styles['boleto-view']}`}>
      {boletoCode ? (
        <>
          <p
            className={`boleto-copy__description ${styles['boleto_copy__description']}`}
          >
            Use o código abaixo para pagar o boleto no app do seu banco.
          </p>
          <textarea
            className={`boleto-code ${styles['boleto_code']}`}
            disabled
            defaultValue={boletoCode}
          />
          <Button.Root
            variation="secondary"
            type="button"
            fullWidth
            size="small"
            onClick={handleCopyBoletoCode}
          >
            Copiar código
          </Button.Root>
        </>
      ) : (
        <>
          <div className={`boleto-coupon ${styles['boleto-coupon']}`}>
            <input
              type="checkbox"
              id="coupon"
              onChange={() => setShowCoupon(!showCoupon)}
            />
            <label htmlFor="coupon">Usar cupom</label>
          </div>

          {showCoupon && (
            <div className={`coupon__container ${styles['coupon__container']}`}>
              <TextField
                id="boleto-coupon"
                className={`coupon__input ${styles['coupon__input']}`}
                label="Cupom"
                placeholder="Cupom"
                value={checkoutData.coupon}
                onChange={(evt) =>
                  updateCheckoutData((oldState) => ({
                    ...oldState,
                    coupon: evt.target.value,
                  }))
                }
              />
              <Button.Root
                size="small"
                variation="secondary"
                onClick={handleApplyDiscounts}
              >
                Aplicar
              </Button.Root>
            </div>
          )}

          <Button.Root
            variation="primary"
            type="button"
            fullWidth
            size="small"
            isLoading={loading}
            disabled={loading}
            onClick={handleGenerateBoletoCode}
          >
            Gerar boleto
          </Button.Root>
        </>
      )}
    </div>
  );
};
